import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  VolumeUp as VolumeUpIcon,
  Headphones as HeadphonesIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const Listening = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [topics, setTopics] = useState([])
  const [currentTopic, setCurrentTopic] = useState('')
  const [sentences, setSentences] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentLevel, setCurrentLevel] = useState('A1')
  const [topicIdMap, setTopicIdMap] = useState({})
  const [currentPage, setCurrentPage] = useState('listening')

  useEffect(() => {
    if (user && userData) {
      setCurrentLevel(userData.level || 'A1')
      loadTopics(userData.level || 'A1')
    }
  }, [user, userData])

  const loadTopics = async (level) => {
    try {
      setLoading(true)
      console.log('Loading topics for level:', level)
      
      // Android uygulamasındaki aynı yapıyı kullan
      const topicsRef = collection(db, 'listening', level, 'groups')
      const topicsSnapshot = await getDocs(topicsRef)
      
      const topicsList = []
      const newTopicIdMap = {}
      
      topicsSnapshot.forEach(doc => {
        const data = doc.data()
        const topicName = data.title || data.topic || `Grup ${doc.id}`
        topicsList.push(topicName)
        newTopicIdMap[topicName] = doc.id
      })
      
      console.log('Topics loaded:', topicsList)
      setTopics(topicsList)
      setTopicIdMap(newTopicIdMap)
      
      // İlk konuyu seç
      if (topicsList.length > 0) {
        setCurrentTopic(topicsList[0])
        loadSentences(level, newTopicIdMap[topicsList[0]])
      }
    } catch (error) {
      console.error('Error loading topics:', error)
      toast.error('Konular yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadSentences = async (level, topicId) => {
    try {
      setLoading(true)
      console.log('Loading sentences for level:', level, 'topic:', topicId)
      
      // Android uygulamasındaki aynı yapıyı kullan
      const sentencesRef = doc(db, 'listening', level, 'groups', topicId)
      const sentencesDoc = await getDoc(sentencesRef)
      
      if (sentencesDoc.exists()) {
        const data = sentencesDoc.data()
        const sentencesList = data.sentences || []
        
        console.log('Sentences loaded:', sentencesList)
        setSentences(sentencesList)
      } else {
        console.log('No sentences found for this topic')
        setSentences([])
        toast.info('Bu konu için henüz cümle bulunmuyor')
      }
    } catch (error) {
      console.error('Error loading sentences:', error)
      toast.error('Cümleler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleTopicChange = (event) => {
    const selectedTopic = event.target.value
    setCurrentTopic(selectedTopic)
    
    // Topic ID'sini bul - Android uygulamasındaki gibi
    const topicId = topicIdMap[selectedTopic]
    if (topicId) {
      loadSentences(currentLevel, topicId)
    }
  }

  const handleBackToListening = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('listening')
    }
  }

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'en-US'
      utterance.rate = 0.8 // Biraz yavaş oku
      
      window.speechSynthesis.speak(utterance)
      toast.success('Cümle okunuyor...')
    } else {
      toast.error('Sesli okuma bu tarayıcıda desteklenmiyor')
    }
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Dinleme Egzersizleri
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card - Reading sayfasındaki gibi */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <HeadphonesIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Dinleme Egzersizleri
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              Seviye: {currentLevel}
            </Typography>
          </CardContent>
        </Card>

        {/* Topic Selection */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              Konu Seçin
            </Typography>
            <FormControl fullWidth>
              <Select
                value={currentTopic}
                onChange={handleTopicChange}
                displayEmpty
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1565c0'
                  }
                }}
              >
                {topics.map((topic, index) => (
                  <MenuItem key={index} value={topic}>
                    {topic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Sentences List */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
                 ) : sentences.length === 0 ? (
           <Card sx={{ 
             mx: 2,
             backgroundColor: 'rgba(255, 255, 255, 0.95)',
             backdropFilter: 'blur(10px)',
             border: '1px solid rgba(255, 255, 255, 0.2)',
             boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
           }}>
             <CardContent sx={{ textAlign: 'center', py: 4 }}>
               <HeadphonesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
               <Typography variant="h6" color="text.secondary" gutterBottom>
                 Henüz Cümle Yok
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Bu konu için henüz dinleme cümlesi eklenmemiş
               </Typography>
             </CardContent>
           </Card>
         ) : (
           <Card sx={{ 
             mx: 2,
             backgroundColor: 'rgba(255, 255, 255, 0.95)',
             backdropFilter: 'blur(10px)',
             border: '1px solid rgba(255, 255, 255, 0.2)',
             boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
           }}>
             <CardContent>
               <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                 Dinleme Cümleleri ({sentences.length})
               </Typography>
              <List>
                {sentences.map((sentence, index) => (
                  <ListItem key={index} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium" color="primary">
                          {sentence.english}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {sentence.turkish}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        startIcon={<VolumeUpIcon />}
                        onClick={() => speakText(sentence.english)}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)'
                          }
                        }}
                      >
                        Dinle
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Info Alert */}
        <Alert severity="info" sx={{ mt: 2, mx: 2 }}>
          Cümleleri dinlemek için "Dinle" butonuna tıklayın. İngilizce cümleyi dinleyip Türkçe anlamını kontrol edin.
        </Alert>
      </Container>
    </SpaceTheme>
  )
}

export default Listening
